import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
// MATERIAL-UI
import Snackbar from '@mui/material/Snackbar';
// Components
import AppHeader from './AppHeader';
import LoadingState from 'components/common/LoadingState';
import SessionExpiredMessage from 'components/common/SessionExpiredMessage';
import SimpleHeader from './SimpleHeader';
// Libs & CONFIG
import AppAuth from 'AppAuth';
import AppFetch from 'fetch/AppFetch';
import { getAppTitle } from 'lib/AppHelpers';
import VersionFetch from 'fetch/VersionFetch';
import versionInfo from 'version.json';
// Constants
import AppConfig from 'appConfig';
import {
  USER_TYPE_ZYWAVE,
  // USER_TYPE_TEST
} from 'lib/config/appConstants';
import { eventTypeFetched } from 'redux/actions/thunks/EventTypesThunk';

const Layout = (props) => {
  const [latestVersionInfo, setLatestVersionInfo] = React.useState(versionInfo);

  const userInfo = useMemo(() => {
    return AppAuth.loggedIn() ? AppFetch.fetchUserInfo() : null;
  });

  useEffect(() => {
    const abortController = new AbortController();

    VersionFetch.fetchLatestVersion(abortController.signal)
      .then((latestVersionInfo) => {
        setLatestVersionInfo(latestVersionInfo);
      })
      .catch((err) => {
        if (err.name !== 'AbortError') {
          console.error('Failed to fetch latest version:', err);
        }
      });

    return () => {
      abortController.abort();
    };
  }, []);

  /*
   * check for latest version and reload the page if a new version is available
   */
  useEffect(() => {
    if (
      AppConfig.enableAutoRefreshNewVersion &&
      versionInfo?.version &&
      latestVersionInfo?.version &&
      versionInfo.version !== latestVersionInfo.version
    ) {
      window.location.reload();
    }
  }, [latestVersionInfo, versionInfo]);

  useEffect(() => {
    if (props?.eventTypes?.length === 0) {
      props.onRefetchEventTypes(props.module);
    }
  }, [props]);

  const handleErrorSnackbarRequestClose = () => {
    if (props.onClearAppError) {
      props.onClearAppError();
    }
  };

  const handleSessionExpiredSnackBarClose = () => {
    // Logout after snackbar is closed
    AppAuth.logout();
  };

  if (!props) {
    return null;
  }

  const { appError, children, isFetching, layoutType, module, onAppError, sessionExpired, title } =
    props;

  const errorSnackBarOpen = Boolean(appError);
  const appMessage = appError ? appError.message : '';

  // Conditionally use new component or original hardcoded message based on enableRedirectZywaveDotCom flag
  const sessionMessage = AppConfig.enableRedirectZywaveDotCom ? (
    <SessionExpiredMessage />
  ) : (
    <div>
      <div>Your session has expired or your account was used to login from another browser.</div>
      <div>You will be redirected to the login page.</div>
    </div>
  );

  let appTitle = 'Zywave';

  appTitle = getAppTitle({ module: module });

  const errorSnackBar = (
    <Snackbar
      open={errorSnackBarOpen && !sessionExpired}
      message={appMessage}
      autoHideDuration={8000}
      onClose={handleErrorSnackbarRequestClose}
      bodyStyle={{ backgroundColor: 'red' }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    />
  );
  const sessionSnackBar = (
    <Snackbar
      bodyStyle={{ height: '100px' }}
      contentStyle={{ height: '100px' }}
      open={sessionExpired}
      message={sessionMessage}
      autoHideDuration={4000}
      onClose={handleSessionExpiredSnackBarClose}
      style={{ height: '100px' }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    />
  );

  const branchDeployed = AppConfig.currentBranch;
  const showBranchDeployCanary =
    branchDeployed &&
    branchDeployed !== 'main' &&
    branchDeployed !== 'release' &&
    process.env.REACT_APP_ENV !== 'prod';

  const branchDeployCanary = (
    <div
      style={{
        backgroundColor: 'yellow',
        padding: '5px',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <b>Branch Deploy: {branchDeployed}</b>
    </div>
  );

  return (
    <>
      {userInfo?.account_type === USER_TYPE_ZYWAVE ? (
        <>
          {showBranchDeployCanary && branchDeployCanary}
          {layoutType === 'Standard' && <AppHeader onAppError={onAppError} />}
          {layoutType === 'Simple' && (
            <SimpleHeader title={title} appName={appTitle} module={module} />
          )}
          {children}
          {isFetching && <LoadingState />}
          {errorSnackBar}
          {sessionSnackBar}
        </>
      ) : (
        <>
          {showBranchDeployCanary && branchDeployCanary}
          {layoutType === 'Standard' && <AppHeader onAppError={onAppError} />}
          {layoutType === 'Simple' && (
            <SimpleHeader title={title} appName={appTitle} module={module} />
          )}
          {children}
          {isFetching && <LoadingState />}
          {errorSnackBar}
          {sessionSnackBar}
        </>
      )}
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    module: state.ModuleState.module,
    eventTypes: state.EventSubscriptionState.eventTypes,
  };
};

const mapDispatchToProps = (dispatch) => ({
  onRefetchEventTypes: (module) => {
    const criterias = {
      application: module?.toLowerCase(),
    };
    dispatch(eventTypeFetched(criterias));
  },
});

Layout.propTypes = {
  appError: PropTypes.object,
  isFetching: PropTypes.bool,
  layoutType: PropTypes.oneOf(['Standard', 'Simple', 'NoHeader']),
  onAppError: PropTypes.func.isRequired,
  onClearAppError: PropTypes.func,
  sessionExpired: PropTypes.bool,
  title: PropTypes.string,
  module: PropTypes.string,
  eventTypes: PropTypes.array,
  onRefetchEventTypes: PropTypes.func,
  children: PropTypes.node,
};

Layout.defaultProps = {
  sessionExpired: false,
  layoutType: 'Standard',
  isFetching: false,
};

export default connect(mapStateToProps, mapDispatchToProps)(Layout);
